import { MatchValue, Operator, Rule } from '@dadohr/shared/domainTypes/rules';
import { RuleDataType } from './types';

/**
 * Data types whose conditions use the set pseudo-operators (isAnyOf, isNoneOf,
 * ...): their value is an array, and the builder expands one condition into a
 * group of scalar conditions before save.
 */
export const usesSetOperators = (
  dataType: RuleDataType | undefined
): boolean => dataType === 'string' || dataType === 'user';

/** Operators that test for an empty value and ignore the condition's `v`. */
export const isBlankOperator = (op: string): boolean =>
  op === 'isBlank' || op === 'isNotBlank';

/**
 * Placeholder the chip input emits for "blank" inside a set-operator value
 * list; the expansion turns it into an isBlank / isNotBlank condition and the
 * group detector puts it back when collapsing the group for display.
 */
export const BLANK_VALUE = '__BLANK__';
export const BLANK_LABEL_STRING = '(blank)';
export const BLANK_LABEL_USER = '(not assigned)';

/** Used for a date field whose config carries no org date format. */
export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

export const OPERATORS_BY_DATA_TYPE: {
  [K in RuleDataType]: Operator[];
} = {
  boolean: ['==', '!='],
  string: [
    'isAnyOf',
    'isNoneOf',
    'containsAnyOf',
    'doesNotContainAnyOf'
  ] as Operator[],
  number: ['==', '!=', '>', '>=', '<', '<='],
  date: [
    'isOn',
    'isNotOn',
    '<',
    '<=',
    '>',
    '>=',
    'isBlank',
    'isNotBlank'
  ] as Operator[],
  user: ['isAnyOf', 'isNoneOf', 'isNotBlank', 'isBlank'] as Operator[]
};

export const OPERATORS_TO_STRING: {
  [K in RuleDataType]: {
    [KK in Operator]: string;
  };
} = {
  boolean: {
    '<': '',
    '<=': '',
    '>': '',
    '>=': '',
    '==': 'is',
    '!=': 'is not',
    '=~': '',
    '!~': '',
    isBlank: '',
    isNotBlank: '',
    isAnyOf: '',
    isNoneOf: '',
    containsAnyOf: '',
    doesNotContainAnyOf: '',
    isOn: '',
    isNotOn: ''
  },
  string: {
    '<': '',
    '<=': '',
    '>': '',
    '>=': '',
    '==': 'is',
    '!=': 'is not',
    '=~': 'contains',
    '!~': 'does not contain',
    isBlank: 'is not set',
    isNotBlank: 'is set',
    isAnyOf: 'is any of',
    isNoneOf: 'is none of',
    containsAnyOf: 'contains any of',
    doesNotContainAnyOf: 'does not contain any of',
    isOn: '',
    isNotOn: ''
  },
  number: {
    '<': 'is less than',
    '<=': 'is less than or equal to',
    '>': 'is greater than',
    '>=': 'is greater than or equal to',
    '==': 'is',
    '!=': 'is not',
    '=~': '',
    '!~': '',
    isBlank: '',
    isNotBlank: '',
    isAnyOf: '',
    isNoneOf: '',
    containsAnyOf: '',
    doesNotContainAnyOf: '',
    isOn: '',
    isNotOn: ''
  },
  date: {
    '<': 'is before',
    '<=': 'is before or on',
    '>': 'is after',
    '>=': 'is on or after',
    '==': 'is',
    '!=': 'is not',
    '=~': '',
    '!~': '',
    isBlank: 'is not set',
    isNotBlank: 'is set',
    isAnyOf: '',
    isNoneOf: '',
    containsAnyOf: '',
    doesNotContainAnyOf: '',
    isOn: 'is on',
    isNotOn: 'is not on'
  },
  user: {
    '<': '',
    '<=': '',
    '>': '',
    '>=': '',
    // Not offered in the picker; a saved (expanded) group is a scalar
    // condition per user, so these label its rows wherever such a group is
    // formatted without being collapsed.
    '==': 'is',
    '!=': 'is not',
    '=~': '',
    '!~': '',
    isBlank: 'is not assigned',
    isNotBlank: 'is assigned',
    isAnyOf: 'is any of',
    isNoneOf: 'is none of',
    containsAnyOf: '',
    doesNotContainAnyOf: '',
    isOn: '',
    isNotOn: ''
  }
};

export const DEFAULT_VALUE_BY_DATA_TYPE: {
  [K in RuleDataType]: MatchValue;
} = {
  boolean: true,
  string: [],
  number: 1,
  date: Date.now(),
  user: []
};

export const LEGAL_VALUE_TYPES_BY_DATA_TYPE: {
  [K in RuleDataType]: Set<string>;
} = {
  boolean: new Set(['boolean']),
  string: new Set(['string', 'object']), // Allow arrays for string values
  number: new Set(['number']),
  date: new Set(['number']),
  user: new Set(['string', 'object']) // Arrays of user ids
};

export const EMPTY_RULE_GROUP: Rule<any> = {
  type: 'all',
  els: []
};
